import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ImageGallery = ({ images = [], beforeImages = [], afterImages = [], isRestoration = false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState('gallery'); // 'gallery' | 'before-after'
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const allImages = isRestoration && viewMode === 'before-after' 
    ? [...beforeImages, ...afterImages] 
    : images.length > 0 ? images : [...beforeImages, ...afterImages];

  const hasBeforeAfter = beforeImages.length > 0 && afterImages.length > 0;

  if (allImages.length === 0) {
    return (
      <div className="aspect-[4/3] bg-stone-100 flex items-center justify-center rounded-sm">
        <p className="text-stone-400">No images available</p>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <div className="space-y-4">
      {/* View Mode Toggle */}
      {hasBeforeAfter && (
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('gallery')}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
              viewMode === 'gallery' 
                ? 'bg-gradient-to-r from-sky-500 to-teal-500 text-white' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
            data-testid="gallery-view-btn"
          >
            Gallery
          </button>
          <button
            onClick={() => setViewMode('before-after')}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
              viewMode === 'before-after' 
                ? 'bg-gradient-to-r from-sky-500 to-teal-500 text-white' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
            data-testid="before-after-view-btn"
          >
            Before & After
          </button>
        </div>
      )}

      {/* Before/After View */}
      {viewMode === 'before-after' && hasBeforeAfter ? (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-stone-500">Before</span>
            <div className="aspect-[4/3] overflow-hidden rounded-sm border border-stone-200">
              <img 
                src={beforeImages[0]} 
                alt="Before restoration" 
                className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                onClick={() => { setCurrentIndex(0); setLightboxOpen(true); }}
              />
            </div>
          </div>
          <div className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-teal-600">After</span>
            <div className="aspect-[4/3] overflow-hidden rounded-sm border border-teal-200">
              <img 
                src={afterImages[0]} 
                alt="After restoration" 
                className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                onClick={() => { setCurrentIndex(beforeImages.length); setLightboxOpen(true); }}
              />
            </div>
          </div>
        </div>
      ) : (
        /* Gallery View */
        <div className="relative">
          <div 
            className="aspect-[4/3] overflow-hidden rounded-sm bg-stone-100 cursor-pointer"
            onClick={() => setLightboxOpen(true)}
          >
            <motion.img
              key={currentIndex}
              src={allImages[currentIndex]}
              alt={`Image ${currentIndex + 1}`}
              className="w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            />
          </div>

          {allImages.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-lg transition-colors"
                data-testid="prev-image-btn"
              >
                <ChevronLeft className="h-5 w-5 text-stone-700" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-lg transition-colors"
                data-testid="next-image-btn"
              >
                <ChevronRight className="h-5 w-5 text-stone-700" />
              </button>
            </>
          )}

          {/* Thumbnail Strip */}
          {allImages.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
              {allImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`flex-shrink-0 w-16 h-16 rounded-sm overflow-hidden border-2 transition-colors ${
                    currentIndex === idx ? 'border-teal-500' : 'border-transparent'
                  }`}
                  data-testid={`thumbnail-${idx}`}
                >
                  <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightboxOpen(false)}
          >
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 p-2 text-white hover:text-stone-300"
              data-testid="close-lightbox"
            >
              <X className="h-8 w-8" />
            </button>

            <img
              src={allImages[currentIndex]}
              alt={`Full size ${currentIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {allImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                >
                  <ChevronLeft className="h-8 w-8 text-white" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                >
                  <ChevronRight className="h-8 w-8 text-white" />
                </button>
              </>
            )}

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
              {currentIndex + 1} / {allImages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImageGallery;
